import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Icons, INavHelp } from '@tecalisdev/infinity';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AudioService } from './audio.service';
import { TextToSpeechService } from './text-to-speech.service';

@Injectable({
	providedIn: 'root',
})
export class NavHelpService {
	public audioService = inject(AudioService);
	private tts = inject(TextToSpeechService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	/** Referencia estable para que el tooltip de rotar cámara se actualice con el idioma activo. */
	private readonly rotateCameraNav: INavHelp = {
		id: NavHelpAction.ROTATE,
		icon: 'icon-rotate-camera',
		tooltip: '',
	};

	private commonNavsHelp: Array<INavHelp>;

	/** Misma referencia que usan conexiones (work-life, irpf) para tooltips actualizados. */
	private readonly connectionsNavItems: Array<INavHelp>;

	constructor() {
		this.commonNavsHelp = [
			{
				id: NavHelpAction.QR,
				icon: 'icon-share',
				tooltip: '',
			},
			{
				id: NavHelpAction.VOLUMEN,
				icon: this.getAudioGuideIcon(),
				selected: this.audioService.audioGuideIsEnabled,
				tooltip: '',
			},
			{
				id: NavHelpAction.LANGUAGE,
				icon: 'icon-language',
				tooltip: '',
			},
			{
				id: NavHelpAction.HELP,
				icon: 'icon-help-circle',
				tooltip: '',
			},
		];

		this.connectionsNavItems = [
			{
				id: NavHelpAction.QR,
				icon: 'icon-share',
				tooltip: '',
			},
			{
				id: NavHelpAction.LANGUAGE,
				icon: 'icon-language',
				tooltip: '',
			},
			{
				id: NavHelpAction.HELP,
				icon: 'icon-help-circle',
				tooltip: '',
			},
		];

		this.refreshNavTooltips();

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshNavTooltips());
	}

	private refreshNavTooltips(): void {
		this.rotateCameraNav.tooltip = this.trans.instant('GENERAL.NAV_HELP.CHANGE_CAMERA');

		const qr = this.commonNavsHelp.find((n) => n.id === NavHelpAction.QR);
		if (qr) qr.tooltip = this.trans.instant('GENERAL.NAV_HELP.SEND_TO_DEVICE');

		const vol = this.commonNavsHelp.find((n) => n.id === NavHelpAction.VOLUMEN);
		if (vol) {
			vol.tooltip = this.getAudioGuideTooltip();
			vol.icon = this.getAudioGuideIcon();
			vol.selected = this.audioService.audioGuideIsEnabled;
		}

		const lang = this.commonNavsHelp.find((n) => n.id === NavHelpAction.LANGUAGE);
		if (lang) lang.tooltip = this.trans.instant('GENERAL.NAV_HELP.LANGUAGE');

		const help = this.commonNavsHelp.find((n) => n.id === NavHelpAction.HELP);
		if (help) help.tooltip = this.trans.instant('GENERAL.NAV_HELP.HELP');

		const cQr = this.connectionsNavItems.find((n) => n.id === NavHelpAction.QR);
		if (cQr) cQr.tooltip = this.trans.instant('GENERAL.NAV_HELP.SEND_TO_DEVICE');
		const cLang = this.connectionsNavItems.find((n) => n.id === NavHelpAction.LANGUAGE);
		if (cLang) cLang.tooltip = this.trans.instant('GENERAL.NAV_HELP.LANGUAGE');
		const cHelp = this.connectionsNavItems.find((n) => n.id === NavHelpAction.HELP);
		if (cHelp) cHelp.tooltip = this.trans.instant('GENERAL.NAV_HELP.HELP');
	}

	public getNavsHelp(withoutIds?: string[]): Array<INavHelp> {
		if (!withoutIds || !withoutIds?.length) return this.commonNavsHelp;

		return this.commonNavsHelp.filter((nav) => !withoutIds.includes(nav.id));
	}

	/** QR + idioma (sin audio ni ayuda), para barra reducida en modo tutorial. */
	public getTutorialNavsHelp(): Array<INavHelp> {
		return this.getNavsHelp([NavHelpAction.VOLUMEN, NavHelpAction.HELP]);
	}

	/** QR + idioma para conexiones en tutorial (sin ayuda contextual). */
	public getConnectionsTutorialNavsHelp(): Array<INavHelp> {
		return this.connectionsNavItems.filter(
			(nav) => nav.id === NavHelpAction.QR || nav.id === NavHelpAction.LANGUAGE,
		);
	}

	get connectionsNavsHelp(): Array<INavHelp> {
		return this.connectionsNavItems;
	}

	public getNavsHelpWithCamera(withoutIds?: string[]): Array<INavHelp> {
		return [this.rotateCameraNav, ...this.getNavsHelp(withoutIds)];
	}

	public getRotateCameraNav(): INavHelp {
		return this.rotateCameraNav;
	}

	public toggleAudioGuide(action: INavHelp): void {
		this.audioService.audioGuideIsEnabled
			? this.audioService.disabledAudioGuide()
			: this.audioService.enabledAudioGuide();

		// Detener de inmediato la síntesis de voz si el audio guía está deshabilitado
		if (!this.audioService.audioGuideIsEnabled) {
			this.audioService.stop();
			this.tts.stop();
		}

		action.selected = this.audioService.audioGuideIsEnabled;
		action.icon = this.getAudioGuideIcon();
		action.tooltip = this.getAudioGuideTooltip();
	}

	private getAudioGuideIcon(): Icons {
		return this.audioService.audioGuideIsEnabled ? 'icon-sound' : 'icon-sound-off';
	}

	private getAudioGuideTooltip(): string {
		return this.trans.instant(
			this.audioService.audioGuideIsEnabled ? 'GENERAL.AUDIO_GUIDE_ON' : 'GENERAL.AUDIO_GUIDE_OFF',
		);
	}
}
